import * as React from 'react'
import { Subscriptions, SubscriptionStatusesToShows } from '@modules/special-products/subscriptions/subscriptions'
import { GastroCardStatusesToShow, GastroVouchers } from '@modules/special-products/gastro-vouchers/gastro-vouchers'
import { NoSpecialProducts } from '@modules/special-products/no-special-products'
import { PackagesVip } from '@modules/special-products/packages-vip/packages-vip'
import { ContentLoader } from '@components/loaders/content-loader'
import { SubscriptionContracts } from '@modules/special-products/subscription-contracts/subscription-contracts'
import { SubscriptionContractStatusesToShow } from '@modules/special-products/subscription-contracts/consts'
import { CashbackVouchers } from '@modules/special-products/cashback-vouchers/cashback-vouchers'
import { useGetCashbackVouchersQuery } from '@api/cashback-vouchers'
import { useClientDetails } from '@hooks/use-client-details'
import { CashbackVoucherStatusesToShow } from '@modules/special-products/cashback-vouchers/consts'
import { CashbackVoucher } from '@models/client'

export const SpecialProductsView = (): JSX.Element => {
  const {
    clientDetails: { subscriptions, gastro_cards, packages_vip, subscription_contracts, urls },
    isLoading: isFetchingClientDetails,
  } = useClientDetails()

  const { data: cashbackVouchers = [] } = useGetCashbackVouchersQuery(urls.cashback_vouchers, {
    skip: !urls.cashback_vouchers,
    refetchOnMountOrArgChange: true,
  })

  const cashbackVouchersToShow = cashbackVouchers.filter((voucher: CashbackVoucher) =>
    CashbackVoucherStatusesToShow.includes(voucher.status),
  )

  const hasSubscriptionContract = !!subscription_contracts.filter(subscriptionContract =>
    SubscriptionContractStatusesToShow.includes(subscriptionContract.status),
  ).length

  const subscriptionsToShow = subscriptions.filter(subscription =>
    SubscriptionStatusesToShows.includes(subscription.status),
  )

  const gastroCardsToShow = gastro_cards.filter(gastroCard => GastroCardStatusesToShow.includes(gastroCard.status))

  const isSpecialProductAdded =
    !!subscriptionsToShow.length ||
    !!gastroCardsToShow.length ||
    !!packages_vip.length ||
    hasSubscriptionContract ||
    !!cashbackVouchersToShow.length

  const {
    gastroCards,
    gastroCards2024,
    gastroCards2025,
    gastroCards2026,
    gastroCards2026October,
    gastroCards2026November,
    gastroCards2027,
    gastroCoupons,
    gastroCardBoost,
    gastroCardsDynamic,
    gastroVouchers,
    gastroCards20,
    gastroCardsBlackWeek2025,
    gastroCardsBlackWeek202520,
    gastroCardsFebruary2026,
    gastroCardsPromo20272028,
  } = React.useMemo(() => {
    const appendNew = (cummulated, newVoucher, kind) => ({
      ...cummulated,
      [kind]: [...cummulated[kind], newVoucher],
    })

    return gastro_cards.reduce(
      (vouchers, voucher) => {
        if (['normal', 'wholesale'].includes(voucher.kind)) return appendNew(vouchers, voucher, 'gastroCards')
        if (voucher.kind === 'gastro_coupon') return appendNew(vouchers, voucher, 'gastroCoupons')
        if (['gastro_card_2024', 'gastro_card_2024_wholesale'].includes(voucher.kind))
          return appendNew(vouchers, voucher, 'gastroCards2024')
        if (['gastro_card_2025', 'gastro_card_2025_wholesale'].includes(voucher.kind))
          return appendNew(vouchers, voucher, 'gastroCards2025')
        if (['gastro_card_boost', 'gastro_card_boost_wholesale'].includes(voucher.kind))
          return appendNew(vouchers, voucher, 'gastroCardBoost')

        if (voucher.kind === 'gastro_card_2026') return appendNew(vouchers, voucher, 'gastroCards2026')
        if (voucher.kind === 'gastro_card_2026_october') return appendNew(vouchers, voucher, 'gastroCards2026October')
        if (voucher.kind === 'gastro_card_2026_november') return appendNew(vouchers, voucher, 'gastroCards2026November')
        if (voucher.kind === 'gastro_card_2027') return appendNew(vouchers, voucher, 'gastroCards2027')
        if (voucher.kind === 'gastro_card_dynamic') return appendNew(vouchers, voucher, 'gastroCardsDynamic')
        if (voucher.kind === 'gastro_card_product_package_endless_holiday')
          return appendNew(vouchers, voucher, 'gastroVouchers')
        if (voucher.kind === 'gastro_card_black_week_2024') return appendNew(vouchers, voucher, 'gastroCards20')
        if (voucher.kind === 'gastro_card_black_week_2025')
          return appendNew(vouchers, voucher, 'gastroCardsBlackWeek2025')
        if (voucher.kind === 'gastro_card_black_week_20')
          return appendNew(vouchers, voucher, 'gastroCardsBlackWeek202520')
        if (voucher.kind === 'gastro_card_promo_february_2026')
          return appendNew(vouchers, voucher, 'gastroCardsFebruary2026')
        if (voucher.kind === 'gastro_card_promo_2027_2028')
          return appendNew(vouchers, voucher, 'gastroCardsPromo20272028')

        return vouchers
      },
      {
        gastroCards: [],
        gastroCards2024: [],
        gastroCards2025: [],
        gastroCards2026: [],
        gastroCards2026October: [],
        gastroCards2026November: [],
        gastroCards2027: [],
        gastroCoupons: [],
        gastroCardBoost: [],
        gastroCardsDynamic: [],
        gastroVouchers: [],
        gastroCards20: [],
        gastroCardsBlackWeek2025: [],
        gastroCardsBlackWeek202520: [],
        gastroCardsFebruary2026: [],
        gastroCardsPromo20272028: [],
      },
    )
  }, [gastro_cards])

  return (
    <div className="mt-3 d-flex flex-column gap-3 bg-light-azure mx-n2 mx-xl-0">
      <ContentLoader isLoading={isFetchingClientDetails}>
        {isSpecialProductAdded ? (
          <>
            {!!subscriptionsToShow.length && <Subscriptions subscriptions={subscriptionsToShow} />}
            {!!packages_vip.length && <PackagesVip packagesVip={packages_vip} />}
            {/*<Promocodes />*/}

            {!!gastroCards.length && (
              <GastroVouchers gastroCards={gastroCards} title="Twoje vouchery Podwójna Radość" />
            )}

            {!!gastroCards20.length && (
              <GastroVouchers gastroCards={gastroCards20} title="Twoje vouchery Podwójna Radość Black Week" />
            )}

            {!!gastroCardsBlackWeek2025.length && (
              <GastroVouchers
                gastroCards={gastroCardsBlackWeek2025}
                title="Twoje vouchery Podwójna Radość Black Week w sierpniu"
              />
            )}

            {!!gastroCardsBlackWeek202520.length && (
              <GastroVouchers
                gastroCards={gastroCardsBlackWeek202520}
                title="Twoje vouchery Podwójna Radość Black Week 2.0"
              />
            )}

            {!!gastroCardsFebruary2026.length && (
              <GastroVouchers
                gastroCards={gastroCardsFebruary2026}
                title="Twoje vouchery Podwójna Radość Oferta Luty 2026"
              />
            )}

            {!!gastroCardsPromo20272028.length && (
              <GastroVouchers
                gastroCards={gastroCardsPromo20272028}
                title="Twoje vouchery Podwójna Radość na lata 2027/2028"
              />
            )}

            {!!gastroCards2024.length && (
              <GastroVouchers gastroCards={gastroCards2024} title="Twoje vouchery Podwójna Radość 2024" />
            )}

            {!!gastroCards2025.length && (
              <GastroVouchers gastroCards={gastroCards2025} title="Twoje vouchery Podwójna Radość 2025" />
            )}

            {!!gastroCards2026.length && (
              <GastroVouchers gastroCards={gastroCards2026} title="Twoje vouchery Podwójna Radość 2026/2027" />
            )}

            {!!gastroCards2026October.length && (
              <GastroVouchers
                gastroCards={gastroCards2026October}
                title="Twoje vouchery Podwójna Radość 2026/2027 Last Minute"
              />
            )}

            {!!gastroCards2026November.length && (
              <GastroVouchers
                gastroCards={gastroCards2026November}
                title="Twoje vouchery Podwójna Radość 2026/2027 Extra Szansa"
              />
            )}

            {!!gastroCards2027.length && (
              <GastroVouchers gastroCards={gastroCards2027} title="Twoje vouchery Podwójna Radość 2027" />
            )}

            {!!gastroCoupons.length && (
              <GastroVouchers gastroCards={gastroCoupons} title="Twoje vouchery Strzał w Dziesiątkę" />
            )}

            {!!gastroCardBoost.length && (
              <GastroVouchers gastroCards={gastroCardBoost} title="Twoje vouchery Doładowanie Smaku" />
            )}

            {!!gastroCardsDynamic.length && (
              <GastroVouchers gastroCards={gastroCardsDynamic} title="Twoje vouchery Podwójna Radość 2025 Plus" />
            )}

            {!!gastroVouchers.length && (
              <GastroVouchers gastroCards={gastroVouchers} title="Twoje vouchery gastronomiczne" />
            )}

            {hasSubscriptionContract && <SubscriptionContracts subscriptionContracts={subscription_contracts} />}

            {!!cashbackVouchersToShow.length && <CashbackVouchers cashbackVouchers={cashbackVouchers} />}
          </>
        ) : (
          <NoSpecialProducts />
        )}
      </ContentLoader>
    </div>
  )
}
